import axios from 'axios';
import { ExamReadinessInfo, UserExam } from 'types/exam';
import {
  EXAM_SCHEDULER_API_URL,
  EXAM_COLLATERAL_API_URL,
  EXAM_STATUS_MANAGEMENT_API_URL,
  USER_EXAM_ENROLL_API,
  USER_EXAM_STATUS_API,
  USER_EXAM_EXPERIENCE_ENROLL_API,
  USER_EXAM_EXPERIENCE_STATUS_API,
  EXAM_FAQ_API,
  EXAM_READINESS_API,
} from '../config/constants';

const { get, post } = axios;

export const getExamCollateral = async (code, language = 'en-US') => {
  if (!code) {
    throw new Error('Course code is required');
  }

  try {
    const collateral = await get(
      `${EXAM_COLLATERAL_API_URL}${code}@@${language}`,
    );
    return collateral.data;
  } catch (error) {
    throw new Error(error);
  }
};

export const getExamFaqPlaylist = async () => {
  try {
    const faqPlaylist = await get(EXAM_FAQ_API);

    return faqPlaylist.data;
  } catch (error) {
    throw new Error(error);
  }
};

export const getExamChecklists = async (code: string) => {
  try {
    const examChecklists = await get<ExamReadinessInfo>(EXAM_READINESS_API, {
      params: { code },
    });

    return examChecklists.data;
  } catch (error) {
    throw new Error(error);
  }
};

export const checkExamStatus = async (code, version) => {
  if (!code || !version) {
    throw new Error('Course code and version are required');
  }

  try {
    const examInfo = await get(`${EXAM_SCHEDULER_API_URL}${code}-${version}`);
    return examInfo.data;
  } catch (error) {
    return { status: 'unenrolled' };
  }
};

export const getUserExams = async (): Promise<UserExam[]> => {
  try {
    const examInfo = await get<{ items: UserExam[] }>(EXAM_SCHEDULER_API_URL);
    return examInfo.data.items ?? [];
  } catch (error) {
    return [];
  }
};

export const getReporteesExamStatus = async (
  users: string[],
  signal?: AbortSignal,
): Promise<UserExam[]> => {
  try {
    const request = { users };
    const examData = await post<{ items: UserExam[] }>(
      EXAM_STATUS_MANAGEMENT_API_URL,
      request,
      {
        signal,
      },
    );
    return examData.data.items ?? [];
  } catch (err) {
    throw err;
  }
};

export const getExamStatus = async (sku, isExperience = false) => {
  if (!sku) {
    throw new Error('Exam sku is required');
  }
  try {
    const examInfo = await post(
      isExperience ? USER_EXAM_EXPERIENCE_STATUS_API : USER_EXAM_STATUS_API,
      { sku },
    );
    return examInfo.data;
  } catch (error) {
    throw new Error(error.response?.data?.error);
  }
};

export const enrollIntoExam = async (
  sku: string,
  isExperience: boolean = false,
) => {
  if (!sku) {
    throw new Error('Exam sku is required');
  }

  try {
    const examInfo = await post(
      isExperience ? USER_EXAM_EXPERIENCE_ENROLL_API : USER_EXAM_ENROLL_API,
      { sku },
    );
    if (!examInfo.data) {
      throw new Error('Could not enroll into the exam.');
    }
    return examInfo.data;
  } catch (error) {
    throw new Error(error.response?.data?.error);
  }
};

export const cancelExam = async (slug: string) => {
  if (!slug) {
    throw new Error('Exam slug is required');
  }

  try {
    await axios.put(`${EXAM_SCHEDULER_API_URL}${slug}/`, {
      operation: 'cancel',
    });
  } catch (error) {
    throw error;
  }
};
